// Mixed module: exports both React components (DateAxis, etc.) and pure
// helpers (colorForSlotId, …). Fast-refresh dislikes the mix but the helpers
// and components have always lived together here since the file was ported
// from frontend/; splitting would touch every import site. Suppress the
// warning at the file level instead.
/* eslint-disable react-refresh/only-export-components */
import React from 'react';

// CTV-457: shared date-axis primitives for the self-serve Scheduler. Ported
// from frontend/src/components/rfp/CreativeScheduleGantt.tsx with the design
// re-tuned for the self-serve surface (no validation overlays, no flight
// markers — the wizard's Scheduler operates on a single campaign window).
// Kept here (rather than imported from frontend/) because frontend/ is a
// separate Vite app with its own build target.

// CTV-457: station-derived palette generator. Given the station's accent
// (and optionally primary / secondary) we derive a small set of easily-
// differentiable colours by walking the colour wheel — the accent's
// saturation and lightness are reused so the palette reads as a family
// rather than a rainbow.

const DEFAULT_PALETTE = [
  '#0f2c64', // dark blue
  '#10b981', // emerald
  '#0f5c57', // dark teal
  '#1a4d7c', // medium blue
  '#34d399', // light emerald
  '#059669', // dark emerald
];

function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / d + 2); break;
      default: h = ((r - g) / d + 4);
    }
    h *= 60;
  }
  return [h, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  const hh = ((h % 360) + 360) % 360;
  const ss = clamp(s, 0, 100) / 100;
  const ll = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * ll - 1)) * ss;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = ll - c / 2;
  let r = 0; let g = 0; let b = 0;
  if (hh < 60) { r = c; g = x; b = 0; }
  else if (hh < 120) { r = x; g = c; b = 0; }
  else if (hh < 180) { r = 0; g = c; b = x; }
  else if (hh < 240) { r = 0; g = x; b = c; }
  else if (hh < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  const toHex = (v: number) => {
    const n = Math.round((v + m) * 255);
    return clamp(n, 0, 255).toString(16).padStart(2, '0');
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Build a palette of N easily-differentiable colours from the station's
 * primary + secondary brand colours. Each anchor produces a "lightness
 * ladder" — same hue and saturation, different lightness steps across a
 * readable range — and the ladders are interleaved so adjacent slots use
 * different hues. With a blue primary and green secondary the palette
 * comes out as [navy, dark-green, mid-blue, mid-green, sky-blue, mint, ...]
 * — clearly a family of two colours rather than a rainbow.
 *
 * Falls back to the default palette only when neither anchor parses.
 */
export function buildStationPalette(
  primaryHex: string | undefined,
  secondaryHex: string | undefined,
  count = 8,
): string[] {
  const anchors = [primaryHex, secondaryHex]
    .map((hex) => hexToRgb(hex ?? ''))
    .filter((rgb): rgb is [number, number, number] => !!rgb)
    .map((rgb) => rgbToHsl(rgb[0], rgb[1], rgb[2]));
  if (anchors.length === 0) return DEFAULT_PALETTE.slice(0, count);

  // Variants per anchor: ceil so two anchors at count=5 produce 3+3 (we'll
  // trim to 5 below). Single-anchor case uses the same lightness ladder.
  const perAnchor = Math.ceil(count / anchors.length);
  // Lightness ladder anchored at the station's mid-tone but spread across
  // 30..75 so dark anchors don't collapse and light anchors don't burn out.
  // For 1 variant we just use the clamped lightness directly so a station
  // with one anchor and one slot keeps its exact brand colour.
  const lightnessSteps = (n: number): number[] => {
    if (n <= 1) return [50];
    const lo = 30;
    const hi = 75;
    const stride = (hi - lo) / (n - 1);
    return Array.from({ length: n }, (_, i) => lo + stride * i);
  };
  const steps = lightnessSteps(perAnchor);

  // For each anchor, produce variants by stepping lightness while keeping
  // hue + saturation locked. Saturation is clamped up so washed-out brand
  // colours still read as distinct from each other.
  const shadesByAnchor: string[][] = anchors.map(([h, s]) => {
    const sat = clamp(s, 50, 90);
    return steps.map((l) => hslToHex(h, sat, l));
  });

  // Interleave so adjacent palette positions belong to different anchors.
  // Slot 0 → anchor 0 darkest, slot 1 → anchor 1 darkest, slot 2 → anchor 0
  // mid, slot 3 → anchor 1 mid, etc. Single-anchor stations collapse to a
  // straight ladder.
  const interleaved: string[] = [];
  outer: for (let i = 0; i < perAnchor; i++) {
    for (let a = 0; a < anchors.length; a++) {
      interleaved.push(shadesByAnchor[a][i]);
      if (interleaved.length === count) break outer;
    }
  }
  return interleaved;
}

/**
 * Stable color per slot when callers don't pass an explicit palette. Used
 * by surfaces that don't have station context (the detail-page chart hover
 * tooltip's read-only fallback, for example). The hash-by-slotId pattern
 * keeps the colour steady across reorder / delete.
 */
export function colorForSlotId(slotId: string, palette: string[] = DEFAULT_PALETTE): string {
  let hash = 0;
  for (let i = 0; i < slotId.length; i++) {
    hash = (hash * 31 + slotId.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

export function enumerateDays(start: string, end: string): string[] {
  if (!start || !end) return [];
  const days: string[] = [];
  const d = new Date(start + 'T00:00:00');
  const endD = new Date(end + 'T00:00:00');
  if (isNaN(d.getTime()) || isNaN(endD.getTime()) || d > endD) return [];
  while (d <= endD) {
    days.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

/**
 * Inclusive day count between two YYYY-MM-DD strings. Returns 0 for an
 * invalid pair (end < start, empty inputs, etc.).
 */
export function dayCount(start: string, end: string): number {
  return enumerateDays(start, end).length;
}

export function formatTickLabel(date: string, windowStart: string, windowEnd: string): string {
  const d = new Date(date + 'T00:00:00');
  const startYear = new Date(windowStart + 'T00:00:00').getFullYear();
  const endYear = new Date(windowEnd + 'T00:00:00').getFullYear();
  if (startYear !== endYear) {
    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
  }
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

interface DateAxisProps {
  windowStart: string;
  windowEnd: string;
  // Cap ticks at this many evenly-spaced labels. Defaults to 6 — readable on
  // mobile (~300px wide) without label collision.
  maxTicks?: number;
}

export const DateAxis: React.FC<DateAxisProps> = ({ windowStart, windowEnd, maxTicks = 6 }) => {
  const days = React.useMemo(() => enumerateDays(windowStart, windowEnd), [windowStart, windowEnd]);
  const total = days.length;
  if (total === 0) return null;

  // Even tick stride so the first and last labels always render and the
  // middle picks N-2 from the interior.
  const ticks: Array<{ date: string; position: number; visible: boolean }> = [];
  if (total === 1) {
    ticks.push({ date: days[0], position: 50, visible: true });
  } else {
    const n = Math.min(maxTicks, total);
    for (let i = 0; i < n; i++) {
      const idx = Math.round((i * (total - 1)) / (n - 1));
      ticks.push({ date: days[idx], position: (idx / (total - 1)) * 100, visible: true });
    }
  }

  return (
    <div className="relative h-4 mt-1 border-t border-slate-300">
      {ticks.map((tick, i) => (
        <div
          key={i}
          className="absolute transform -translate-x-1/2"
          style={{ left: `${tick.position}%`, top: 0 }}
        >
          <div className="w-px h-1.5 bg-slate-400 mx-auto" />
          <div className="text-[9px] text-slate-500 whitespace-nowrap" style={{ marginTop: '1px' }}>
            {formatTickLabel(tick.date, windowStart, windowEnd)}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Returns the [left%, width%] pair for a bar spanning [barStart, barEnd]
 * within the window [windowStart, windowEnd]. The bar is clamped to the
 * window when it extends past either edge.
 */
export function dateRangeToPctSpan(
  barStart: string,
  barEnd: string,
  windowStart: string,
  windowEnd: string,
): { leftPct: number; widthPct: number } {
  const days = enumerateDays(windowStart, windowEnd);
  if (days.length === 0) return { leftPct: 0, widthPct: 0 };
  const clampedStart = barStart < windowStart ? windowStart : barStart > windowEnd ? windowEnd : barStart;
  const clampedEnd = barEnd > windowEnd ? windowEnd : barEnd < windowStart ? windowStart : barEnd;
  const startIdx = days.indexOf(clampedStart);
  const endIdx = days.indexOf(clampedEnd);
  if (startIdx < 0 || endIdx < 0) return { leftPct: 0, widthPct: 0 };
  const total = days.length;
  return {
    leftPct: (startIdx / total) * 100,
    widthPct: ((endIdx - startIdx + 1) / total) * 100,
  };
}

// CTV-476: weight-distribution helpers (distributeEvenWeights,
// rebalanceForAddedSlot, rebalanceForRemovedSlot, setWeightAndRebalance) were
// removed — they modelled a single per-creative scalar that no longer exists.
// Per-segment shares are managed by `shared/rotationSegments.ts`.

